// ApiServiceShared.js - Service API partagé pour les fonctionnalités communes
import { ENV_CONFIG } from '../config/environment';

/**
 * Service partagé pour les fonctionnalités API communes à tous les services
 */
export class ApiServiceShared {
  /**
   * Récupérer les statistiques publiques de la page d'accueil
   * Route: GET /api/public/statistics
   * 
   * @returns {Promise<{success: boolean, data?: {students: number, teachers: number, circles: number, parents: number}, error?: string}>}
   */
  static async getPublicStatistics() {
    try {
      const baseUrl = ENV_CONFIG.apiBaseUrl;
      const url = `${baseUrl}/api/public/statistics`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const responseText = await response.text();
      let data = null;

      try {
        data = responseText ? JSON.parse(responseText) : null;
      } catch (parseError) {
        return {
          success: false,
          error: `Réponse non-JSON du serveur (status: ${response.status}): ${responseText.substring(0, 100)}`
        };
      }

      if (!response.ok) {
        const errorMessage = data?.message || data?.error || response.statusText || `HTTP error! status: ${response.status}`;
        return {
          success: false,
          error: errorMessage
        };
      }

      return {
        success: true,
        data: data?.data || data
      };
    } catch (error) {
      console.error('❌ ApiServiceShared - getPublicStatistics - Erreur:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la récupération des statistiques'
      };
    }
  }
  /**
   * Soumettre un message de contact public
   * Route: POST /api/public/contact
   */
  static async submitContact({ full_name, phone, email, subject, message }) {
    try {
      const baseUrl = ENV_CONFIG.apiBaseUrl;
      const response = await fetch(`${baseUrl}/api/public/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ full_name, phone, email, subject, message }),
      });
      const responseText = await response.text();
      let data = null;
      try { data = responseText ? JSON.parse(responseText) : null; } catch (_) { /**/ }
      if (!response.ok) {
        return { success: false, statusCode: response.status, message: data?.message || `Erreur ${response.status}` };
      }
      return { success: true, data: data?.data || data };
    } catch (error) {
      return { success: false, message: 'Impossible de joindre le serveur.' };
    }
  }

  /**
   * Récupérer l'année scolaire ouverte aux inscriptions
   * Route: GET /api/public/inscription/year
   */
  static async getInscriptionYear() {
    try {
      const baseUrl = ENV_CONFIG.apiBaseUrl;
      const response = await fetch(`${baseUrl}/api/public/inscription/year`, { method: 'GET' });
      const text = await response.text();
      let data = null;
      try { data = text ? JSON.parse(text) : null; } catch (_) { /**/ }
      if (!response.ok) return { success: false };
      return { success: true, data: data?.data || null };
    } catch {
      return { success: false };
    }
  }

  /**
   * Vérifier les credentials d'un parent existant et retourner ses enfants
   * Route: POST /api/public/inscription/verify-parent
   */
  static async verifyParent(username, password) {
    try {
      const baseUrl = ENV_CONFIG.apiBaseUrl;
      const response = await fetch(`${baseUrl}/api/public/inscription/verify-parent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      const responseText = await response.text();
      let data = null;
      try { data = responseText ? JSON.parse(responseText) : null; } catch (_) { /**/ }
      if (!response.ok) {
        return { success: false, statusCode: response.status, message: data?.message || `Erreur ${response.status}` };
      }
      return { success: true, data: data?.data || data };
    } catch (error) {
      return { success: false, message: 'Impossible de joindre le serveur.' };
    }
  }

  /**
   * Soumettre une demande d'inscription publique
   * Route: POST /api/public/inscription
   */
  static async submitInscription(payload) {
    try {
      const baseUrl = ENV_CONFIG.apiBaseUrl;
      const response = await fetch(`${baseUrl}/api/public/inscription`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const responseText = await response.text();
      let data = null;
      try { data = responseText ? JSON.parse(responseText) : null; } catch (_) { /**/ }
      if (!response.ok) {
        return { success: false, statusCode: response.status, message: data?.message || `Erreur ${response.status}`, errors: data?.errors };
      }
      return { success: true, data: data?.data || data };
    } catch (error) {
      return { success: false, message: 'Impossible de joindre le serveur.' };
    }
  }

  /**
   * Mettre à jour uniquement repeatNewHifz d'un enregistrement Coran
   * Route: PATCH /api/quran-records/:id/repeat-new-hifz
   * 
   * @param {number} recordId - ID de l'enregistrement Coran
   * @param {number} repeatNewHifz - Valeur de repeat_new_hifz à sauvegarder
   * @param {string} token - Token d'authentification
   * @param {Function} handleAuthError - Fonction pour gérer les erreurs d'authentification
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   */
  static async updateRepeatNewHifz(recordId, repeatNewHifz, token, handleAuthError = null) {
    try {
      if (!token) {
        token = localStorage.getItem('app_token');
      }
      
      if (!token) {
        return {
          success: false,
          error: 'Token d\'authentification manquant'
        };
      }

      // Validation
      const repeatNewHifzValue = parseInt(repeatNewHifz);
      if (isNaN(repeatNewHifzValue) || repeatNewHifzValue < 0) {
        return {
          success: false,
          error: 'repeatNewHifz doit être un entier positif'
        };
      }

      const baseUrl = ENV_CONFIG.apiBaseUrl;
      const url = `${baseUrl}/api/quran-records/${recordId}/repeat-new-hifz`;
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };

      const backendData = {
        repeatNewHifz: repeatNewHifzValue
      };

      const response = await fetch(url, {
        method: 'PATCH',
        headers: headers,
        body: JSON.stringify(backendData)
      });

      const responseText = await response.text();
      let data = null;

      try {
        data = responseText ? JSON.parse(responseText) : null;
      } catch (parseError) {
        return {
          success: false,
          error: `Réponse non-JSON du serveur (status: ${response.status}): ${responseText.substring(0, 100)}`
        };
      }

      if (!response.ok) {
        const errorMessage = data?.message || data?.error || response.statusText || `HTTP error! status: ${response.status}`;
        
        // Vérifier si c'est une erreur d'authentification (401 ou 403)
        if ((response.status === 401 || response.status === 403) && handleAuthError) {
          handleAuthError(response, errorMessage);
          return {
            success: false,
            error: 'Token d\'authentification invalide'
          };
        }
        
        return {
          success: false,
          error: errorMessage
        };
      }

      return {
        success: true,
        data: data?.data || data,
        message: data?.message || 'repeat_new_hifz mis à jour avec succès'
      };
    } catch (error) {
      console.error('❌ ApiServiceShared - updateRepeatNewHifz - Erreur:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la mise à jour de repeat_new_hifz'
      };
    }
  }
}

